import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { separateContexts } from '../../common/filtering';
import { DataManagementContext } from '../../components/viewers/DataManagementViewer';
import { SettingsViewerContext } from '../../components/viewers/FullSettingsViewer';
import { formHandlers, formInitialState, formReducer } from '../reducers/FormReducer';
import { BaseSettingsContext } from './BaseSettingsContext';
import { ClientInfoContext } from './ClientInfoContext';
import { CreateContext } from './CreateContext';
import { DataContext } from './DataContext';
import { DataModifyContext } from './DataModifyContext';
import { FrameworkContext } from './FrameworkContent';
import { HelpContext } from './HelpContext';
import { ParentContext } from './ParentContext';
import { SettingParentContext } from './SettingParentContext';
import { UiItemContext } from './UiItemContext';

const WAIT_INTERVAL = 500;

/**
 *  
 * @returns The `FormContext` Context/Provider with FormItems or SettingsForm passed in
 */
export const FormContext = createContext();

/**
 * 
 * @param {object} props (formProps, vpsa, swipedItem)
 * @returns The `FormContext` Context/Provider with FormItems or SettingsForm passed in
 */
const FormProvider = (props) => {

  const { formKey, formProps, vpsa, swipedItem } = props
  const { form_handlers: _form_handlers } = formProps ?? {}

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, handlers, fns, settings } = parentContext ?? {}
  const { currentSettings_state } = states ?? {}
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});

  // pageContext    
  const { themeColors, themeItems, dataOptions } = aps_global ?? {}

  // contexts
  const baseSettingsContext = useContext(BaseSettingsContext)
  const createContext = useContext(CreateContext);
  const dataContext = useContext(DataContext);
  const dataManagementContext = useContext(DataManagementContext);
  const dataModifyContext = useContext(DataModifyContext)
  const frameworkContext = useContext(FrameworkContext);
  const helpContext = useContext(HelpContext);
  const settingsParentContext = useContext(SettingParentContext)
  const settingsViewerContext = useContext(SettingsViewerContext);
  const clientInfoContext = useContext(ClientInfoContext)
  const uiItemContext = useContext(UiItemContext);

  // frameworkContext
  const { framework_state } = frameworkContext ?? {}
  const { desktopMode } = framework_state ?? {}

  // dataManagementContext
  const { dataManagement_state } = dataManagementContext ?? {}
  const { viewItem_preview: viewItemPreview_data } = dataManagement_state ?? {}

  // dataManagementContext
  const { dataModify_state, dataModify_handlers } = dataModifyContext ?? {}
  const { allowImage } = dataModify_state ?? {}

  // settingsViewerContext
  const { settingsViewer_state } = settingsViewerContext ?? {}
  const { viewItem_preview: viewItemPreview_settings } = settingsViewer_state ?? {}

  const { item_state } = uiItemContext ?? {}
  const { modifyActionType, viewItem } = item_state ?? {}

  const { create_handlers } = createContext ?? {}

  const isDataContext = dataContext ? true : false

  const contexts = {
    baseSettingsContext: baseSettingsContext ?? {},
    dataContext: dataContext ?? {},
    dataManagementContext: dataManagementContext ?? {},
    dataModifyContext: dataModifyContext ?? {},
    frameworkContext: frameworkContext ?? {},
    helpContext: helpContext ?? {},
    settingsParentContext: settingsParentContext ?? {},
    settingsViewerContext: settingsViewerContext ?? {},
    staticViewsClientContext: clientInfoContext ?? {},
    uiItemContext: uiItemContext ?? {},
  }

  const { states: _states, handlers: _handlers, fns: _fns } = separateContexts(contexts)

  const formParent_contexts = {
    states: { ..._states, ...states },
    handlers: { ..._handlers, ...handlers },
    fns: { ..._fns, ...fns },
    settings
  }

  const init_state = {
    ...props,
    allowImage,
    dataOptions,
    desktopMode,
    isDataContext,
    modifyType: dataModify_state ? dataModify_state.modifyType : null,
    modifyActionType,
    swipedItem,
    themeColors,
    themeItems,
    vpsa,
    showAll: true,
    formKey,
    viewItem,
  }

  const [form_state, form_dispatch] = useReducer(formReducer, formInitialState(init_state));

  const form_handlers = useMemo(
    () => formHandlers(form_dispatch),
    [form_dispatch]
  );

  const { data_localForm, dataHasChanged, propItemData, form_handlers: form_handlers_fs } = form_state ?? {}


  // const getSettingsUpdateProps = () => {

  //   const { updateProps: updateProps_form } = formProps ?? {}

  //   const { data_state } = dataContext ?? {}
  //   const { updateProps: updateProps_data } = data_state ?? {}

  //   // set the update props depending on origin
  //   let ups = updateProps_data
  //   if (!ups && updateProps_form) { ups = updateProps_form }
  //   if (updateProps_data) { ups = updateProps_data }

  //   return ups
  // }

  useEffect(() => {
    form_handlers.handleSet_formProps(formProps, formKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [formProps, formKey]);

  useEffect(() => {
    if (data_localForm) {
      const timer = setTimeout(() => triggerChange(data_localForm), WAIT_INTERVAL);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data_localForm]);

  /**
   * Handles the form change AFTER the WAIT_INTERVAL
   * @param {object} fd 
   */
  const triggerChange = () => {

    if (dataHasChanged) {

      // create_handlers
      if (create_handlers && create_handlers.handleFormDataChange_create) {
        create_handlers.handleFormDataChange_create({ data_form: data_localForm })
      }

      // dataModify_handlers
      if (dataModify_handlers && dataModify_handlers.handleFormDataChange_dataModify) {
        const dmcp = { data_form: data_localForm, propItemData }
        dataModify_handlers.handleFormDataChange_dataModify(dmcp)
      }

      // form_handlers
      if (form_handlers && form_handlers.handleFormDataChange_local) {
        form_handlers.handleFormDataChange_local(data_localForm)
      }

      if (form_handlers_fs && form_handlers_fs.handleFormDataChange_parent) {
        form_handlers_fs.handleFormDataChange_parent(data_localForm)
      }

      // _form_handlers
      if (_form_handlers && _form_handlers.handleFormDataChange_local) {
        // appFromReducer
        _form_handlers.handleFormDataChange_local(data_localForm)
      }

    }
  }


  const formContext = () => <FormContext.Provider value={{ form_state, form_handlers, formParent_contexts }}>
    {props.children}
  </FormContext.Provider>

  return formContext()
};

export default FormProvider